import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AppService } from '@app/app.service';
import { InformModalComponent } from '@modules/shared/modal/inform-modal/inform-modal.component';
import { Breadcrumb, BreadcrumbService } from '@modules/shared/services/breadcrumb.service';
import { DialogService } from '@modules/shared/services/dialog.service';
import { HeaderService } from '@modules/shared/services/header.service';
import { LocaleService } from '@modules/shared/services/locale.service';
import { PressKitPageService } from '@pages/press-kit-page/press-kit-page.service';
import { Locale } from 'src/environments/language.config';

import { PressKit } from '../adapter/press-kit.adapter';

@Injectable({
  providedIn: 'root',
})
export class PressKitResolver implements Resolve<Observable<PressKit | null>> {
  private appService = inject(AppService);
  private dialogService = inject(DialogService);
  private localeService = inject(LocaleService);
  private pressKitPageService = inject(PressKitPageService);
  private router = inject(Router);
  private translateService = inject(TranslateService);

  public resolve(route: ActivatedRouteSnapshot): Observable<PressKit | null> {
    const pressKitId = route.paramMap.get('pressKitId');
    if (!pressKitId) {
      this.router.navigate(['404'], { skipLocationChange: true });
      return EMPTY;
    }

    return this.pressKitPageService.getPressKit(pressKitId).pipe(
      map((result) => {
        if (result.error?.type === 'NOT_FOUND') {
          this.router.navigate(['404'], { skipLocationChange: true });
          return null;
        }

        if (result.error?.type === 'HTTP_ERROR') {
          this.router.navigate(['500'], { skipLocationChange: true });
          return null;
        }

        if (result.error?.type === 'NO_TRANSLATION') {
          const isEnglish = this.translateService.getCurrentLang() === Locale.EN;
          const targetLanguage = isEnglish ? Locale.DE : Locale.EN;

          const actionLabel =
            targetLanguage === Locale.DE
              ? 'ERRORS.API.NO_TRANSLATION.ACTION_DE'
              : 'ERRORS.API.NO_TRANSLATION.ACTION_EN';

          this.dialogService.openNext({
            component: InformModalComponent,
            disableCloseBtn: true,
            disableBackdropClose: true,
            inputs: {
              headline: 'ERRORS.API.NO_TRANSLATION.HEADLINE',
              message: 'ERRORS.API.NO_TRANSLATION.TEXT',
              actionLabel,
            },
            outputs: {
              requestAction: () => {
                this.dialogService.close();
                this.localeService.changeLanguage(targetLanguage);
              },
            },
          });

          return null;
        }

        const pressKit = result.data;
        if (!pressKit) return null;

        this.appService.darkBackgroundPage();
        this.appService.setTitle(`${pressKit.title} | Mercedes-Benz Media`);

        return pressKit;
      })
    );
  }
}

@Injectable({
  providedIn: 'root',
})
export class PressKitBreadcrumbResolver implements Resolve<Observable<Breadcrumb[] | Breadcrumb | undefined>> {
  private pressKitPageService = inject(PressKitPageService);
  private headerService = inject(HeaderService);
  private breadcrumbService = inject(BreadcrumbService);

  public resolve(route: ActivatedRouteSnapshot): Observable<Breadcrumb[] | Breadcrumb | undefined> {
    const pressKitId = route.paramMap.get('pressKitId');
    if (pressKitId) {
      if (this.breadcrumbService.keepBreadcrumbs) {
        return of({ label: 'PRESS_KIT', link: `/press-kit/${pressKitId}` });
      } else {
        // fetch presskit to build "default" breadcrumbs for this article
        return this.pressKitPageService.getPressKit(pressKitId).pipe(
          switchMap((result) => {
            const pressKit = result.data;
            if (pressKit) {
              if (pressKit.topicId) {
                return this.headerService.findNavigationMenuById(pressKit.topicId).pipe(
                  switchMap((menu) => {
                    if (menu) {
                      return this.headerService.navigationMenuPath$(menu).pipe(
                        map((topicPath) => {
                          return [
                            ...topicPath.map((item) => ({
                              label: `MENU.${item.id}.NAME`,
                              link: `MENU.${item.id}.PATH`,
                            })),
                            { label: 'PRESS_KIT', link: `/press-kit/${pressKitId}` },
                          ];
                        })
                      );
                    } else {
                      return of([{ label: 'PRESS_KIT', link: `/press-kit/${pressKitId}` }]);
                    }
                  })
                );
              } else {
                return of([{ label: 'PRESS_KIT', link: `/press-kit/${pressKitId}` }]);
              }
            } else {
              return of(undefined);
            }
          })
        );
      }
    } else {
      return of(undefined);
    }
  }
}
